import { isTodoFolderDescriptor } from './typeGuards';
import type { TodoDescriptor } from '../schema/TodoDescriptor';
import type { Todo } from 'owa-todo-types';
import type { TodoFolderDescriptor } from '../schema/TodoFolderDescriptor';

/**
 * In some (hopefully rare) cases we need to update the TodoDescriptor with data from the fetched result
 * before we can use the TodoDescriptor to upsert data into the store. This should not be the common case.
 *
 * We use createUpsertDescriptor to updated the TodoDescriptor, and we use
 * getUpsertDescriptor to get the updated descriptor before we getTasks.
 *
 */

let defaultFolderId;

export function createUpsertDescriptor(
    descriptor: TodoDescriptor,
    response: Todo[]
): TodoDescriptor {
    if (isTodoFolderDescriptor(descriptor)) {
        return createUpsertDescriptorFolderDescriptor(descriptor, response);
    } else {
        return descriptor;
    }
}

/**
 * When we fetch todo items for the default folder, sometimes we do not have a folderId.
 * In this case, we have to update the descriptor with the correct folderId which we get from the todos response
 * before we upsert these todos.
 * @param descriptor the descriptor
 * @param response the todos in the response
 */
function createUpsertDescriptorFolderDescriptor(
    descriptor: TodoFolderDescriptor,
    response: Todo[]
): TodoDescriptor {
    // In the case where a folderId is not provided in the descriptor, the default folder is being requested.
    const copyDesc = { ...descriptor };
    if (!copyDesc.folderId) {
        // Set the defaultFolderId so that we can use it when getUpsertDescriptor
        // is called with a null folderId
        defaultFolderId = getFolderId(response);
        copyDesc.folderId = defaultFolderId;
    }
    return copyDesc;
}

export function getUpsertDescriptor(descriptor: TodoDescriptor) {
    if (isTodoFolderDescriptor(descriptor)) {
        return getUpsertDescriptorForFolder(descriptor);
    } else {
        return descriptor;
    }
}

/**
 * Get the descriptor used to upsert the the todos for the folder. This should be
 * the same as descriptor, unless the TodoDescriptor does not have a folderId.
 * In this case we update the folderId to the defaultFolderId.
 *
 * Usually, requests for the default folder items made with a folderId
 * and without a folderId will be added to the store with the same descriptor. In these cases we will not
 * re-request this data.
 * The exception to this rule will be when the default folder task list is empty. In this case the empty tasks will be upserted with a null
 * folderId, for the request made with no folderId, and it will be upserted with a folderId in the case with a folderId. This leaves us with no
 * way to associate these two requests for the default folder items. For this reason, requests made for the default folder items with and without
 * a folderId should be regarded as two seperate requests.
 * @param descriptor
 */
function getUpsertDescriptorForFolder(descriptor: TodoFolderDescriptor) {
    const descCopy = { ...descriptor };
    if (!descriptor.folderId) {
        descCopy.folderId = defaultFolderId;
    }
    return descCopy;
}

export function getFolderId(response: Todo[]): string | null {
    if (response.length > 0) {
        return response[0].ParentFolderId;
    }
    // In cases where there are not items in the response, we upsert items to the cache
    // with a null folderId.
    return null;
}
